import { forwardRef, useEffect, useRef, useState } from "react";

/**
 * Wraps content that fades/rises into place the first time it scrolls
 * into view. Pair with the `.reveal` (whole-block) or `.reveal-children`
 * (staggered children via --i) CSS utilities in index.css.
 *
 * Forwards its ref to the rendered element (merged with the internal one
 * used for the IntersectionObserver) so callers can still reach the real
 * DOM node — e.g. ProductRail tracks scroll position on it.
 */
const Reveal = forwardRef(({ as: Tag = "div", className = "", children, ...rest }, forwardedRef) => {
  const innerRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const setRefs = (node) => {
    innerRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  return (
    <Tag ref={setRefs} className={`${className}${inView ? " reveal-in" : ""}`} {...rest}>
      {children}
    </Tag>
  );
});

export default Reveal;
